import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [claimsRoles, setClaimsRoles] = useState([]); // roles from custom claims
  const [profile, setProfile] = useState(null);       // users/{uid} doc
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();

    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setProfile(null);
      setClaimsRoles([]);

      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // 1) Custom claims (authoritative for routing)
        const tokenResult = await user.getIdTokenResult(true);
        const tokenRoles = tokenResult.claims?.roles;
        // Support either { admin: true, ... } or ["admin", ...]
        const rolesFromClaims = Array.isArray(tokenRoles)
          ? tokenRoles
          : tokenRoles && typeof tokenRoles === "object"
            ? Object.keys(tokenRoles).filter(k => !!tokenRoles[k])
            : [];
        setClaimsRoles(rolesFromClaims);

        // 2) Firestore profile (fallback + UI data)
        const snap = await getDoc(doc(db, "users", user.uid));
        setProfile(snap.exists() ? snap.data() : null);
      } catch (e) {
        console.error("AuthContext load error:", e);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  // Final roles used by app: prefer claims, else Firestore
  const profileRoles = Array.isArray(profile?.roles) ? profile.roles : [];
  const roles = useMemo(
    () => (claimsRoles.length ? claimsRoles : profileRoles),
    [claimsRoles, profileRoles]
  );

  const value = { currentUser, roles, profile, loading };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
